import React, { useState, Fragment, useEffect } from 'react';
import { useDispatch, useSelector, connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Box from '@mui/material/Box';
import {
  Paper,
  Grid,
  Alert,
  AlertTitle,
  Dialog,
  DialogActions,
  DialogContent,
} from '@mui/material';
import Form1 from './Form_1';
import Form2 from './Form_2';
import Form3 from './Form_3';
import Form4 from './Form_4';
import Form5 from './Form_5';
import Form6 from './Form_6';
import Form7 from './Form_7';
import Success from './Success';
import { setErrorAction } from 'store/actions/claimFormActions';
import { Button, styles, IconGraphy, Typography, colors } from '@leapeasy/ui-kit';
import FormConfirmTenant from './FormConfirmTenant';
import FormComments from './FormComments';
import { CustomStepperWrapper } from 'components/ClaimForm/CustomWrapper';
import CustomStepper from 'components/ClaimForm/Stepper';
import styled from '@emotion/styled';

// Actions
import {
  setDataAction,
  getLandlordAction,
  getApartmentAction,
  getRiderAction,
  getTenantsAction,
  checkPolicyDuplicatedAction,
  setInitiateAction,
} from 'store/actions/claimFormActions';
import { isPortalPolicyPrefill } from 'utils/policyEntryMode';
import { selectClaimFormState, scrollClaimFormToTop } from 'utils/claimFormState';
import { getLandlordStateById } from 'store/sagas/APIs/claim';

const steps = [
  {
    label: '1',
    title: 'Locate Your Apartment',
  },
  {
    label: '2',
    title: 'Resident Confirmation',
  },
  {
    label: '3',
    title: 'Type of Claim',
  },
  {
    label: '4',
    title: 'Claim Detail',
  },
  {
    label: '5',
    title: 'Contact Information',
  },
  {
    label: '6',
    title: 'Payment Method',
  },
  {
    label: '7',
    title: 'Document Upload',
  },
  {
    label: '8',
    title: 'Additional Comments',
  },
  {
    label: '9',
    title: 'Review',
  },
];

/** True once the user has advanced past step 1 or entered claim-progress fields. Prefill alone does not count. */
export function claimFormHasProgress(formData, activeStep, maxReachedStep) {
  if (activeStep > 0 || maxReachedStep > 0) {
    return true;
  }
  const data = formData?.data;
  if (!data || typeof data !== 'object') {
    return false;
  }
  if (data.claimType && Object.values(data.claimType).some(Boolean)) {
    return true;
  }
  if (data.paymentAgreement || data.relatedExpense || data.acceptableDocument) {
    return true;
  }
  if (data.damageType && Object.values(data.damageType).some(Boolean)) {
    return true;
  }
  if (data.placeDamages && Object.values(data.placeDamages).some(Boolean)) {
    return true;
  }
  if (data.contactName || data.contactEmail || data.contactPhone || data.contactTitle) {
    return true;
  }
  if (data.additional_comment && String(data.additional_comment).trim()) {
    return true;
  }
  return false;
}

function ClaimForm(props) {
  const formData = useSelector(selectClaimFormState);
  const { actions, data, onClose } = props;

  useEffect(() => {
    let cancelled = false;

    const initializePortalPrefill = async () => {
      // Clear stale claimId / draft from a previous submission in this session.
      actions.setInitiateAction();

      if (!data?.landlord_id) {
        return;
      }

      let billingState = data.billingState || data.billing_state || null;
      if (!billingState) {
        try {
          const response = await getLandlordStateById(data.landlord_id);
          billingState = response?.state || null;
        } catch (_error) {
          // Subsequent landlord/property requests will surface a dialog error if state is required.
        }
      }

      if (cancelled) {
        return;
      }

      const policyStartDate = data.coverage_start_date || data.lease_start_date;
      const policyEndDate = data.coverage_end_date || data.lease_end_date;

      const prefillData = {
        state: billingState,
        landlord: data.landlord_id,
        apartment: data.apartment_building_id,
        policy: data.app_id,
        riderName: data.rider_id,
        landlordName: data.lanlord_account_name || data.landlordName,
        propertyName: data.apartment_building_name || data.propertyName,
        policyStartDate,
        policyEndDate,
        leaseStartDate: data.lease_start_date,
        leaseEndDate: data.lease_end_date,
        tenant_1_name: data.tenant_1_name,
        tenant_2_name: data.tenant_2_name,
        tenant_3_name: data.tenant_3_name,
        tenant_4_name: data.tenant_4_name,
      };

      actions.setDataAction(prefillData);
      if (billingState) {
        actions.getLandlordAction({ state: billingState });
        actions.getApartmentAction({
          landlordId: data.landlord_id,
          state: billingState,
        });
      }
      actions.getRiderAction({
        propertyId: data.apartment_building_id,
      });
      actions.checkPolicyDuplicatedAction({ riderId: data.app_id });
      actions.getTenantsAction(data.app_id);
    };

    initializePortalPrefill();

    return () => {
      cancelled = true;
    };
  }, []);
  const dispatch = useDispatch();
  const isPortalPrefill = isPortalPolicyPrefill(data);
  // Modal flags
  const [policyFlag, setPolicyFlag] = useState(false);

  const [activeStep, setActiveStep] = useState(0);
  const [maxReachedStep, setMaxReachedStep] = useState(0);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  useEffect(() => {
    scrollClaimFormToTop();
  }, [activeStep]);

  // Track farthest step visited so users can jump forward again after going back.
  useEffect(() => {
    setMaxReachedStep((prevMax) => Math.max(prevMax, activeStep));
  }, [activeStep]);

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  /** Jump to a previously reached step only — does not clear Redux form data. */
  const handleStep = (stepIndex) => {
    if (typeof stepIndex !== 'number' || stepIndex < 0 || stepIndex > maxReachedStep) {
      return;
    }
    setActiveStep(stepIndex);
  };

  const exitClaimForm = () => {
    actions.setInitiateAction();
    setDiscardConfirmOpen(false);
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  const handleCloseClick = () => {
    if (typeof onClose !== 'function') {
      return;
    }
    // Only skip confirm on the post-submit success screen (not a stale claimId from an earlier filing).
    if (activeStep >= steps.length) {
      exitClaimForm();
      return;
    }
    if (claimFormHasProgress(formData, activeStep, maxReachedStep)) {
      setDiscardConfirmOpen(true);
      return;
    }
    exitClaimForm();
  };

  useEffect(() => {
    if (formData?.policyArray && formData.policyArray.length === 0) {
      setPolicyFlag(true);
    }
  }, [formData]);
  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {typeof onClose === 'function' && (
        <CloseBar>
          <CloseIconButton
            type="button"
            aria-label="Close claim form"
            title="Close"
            onClick={handleCloseClick}
          >
            <IconGraphy icon="General.Clear" />
          </CloseIconButton>
        </CloseBar>
      )}
      <Grid item md={12} sx={{ my: 2, width: '100%' }}>
        <Paper
          sx={{ display: 'flex', flexDirection: 'column', border: '1px solid white', boxShadow: 0 }}
        >
          <>
            <Box
              sx={{
                borderRadius: styles.borderRadius.small,
                flexGrow: 0,
                flexShrink: 1,
              }}
            >
              {activeStep < steps.length && (
                <CustomStepperWrapper>
                  <CustomStepper
                    steps={steps}
                    activeStep={activeStep}
                    maxReachedStep={maxReachedStep}
                    onStepClick={handleStep}
                  />
                </CustomStepperWrapper>
              )}
            </Box>
          </>

          <Box
            display="flex"
            flexDirection="column"
            padding={{ xs: 0 }}
            gap="20px"
            sx={{ flexGrow: 1, width: '100%' }}
          >
            <Box sx={{ width: '100%' }}>
              <Fragment>
                {activeStep === 0 && (
                  <Form1
                    handleNext={handleNext}
                    handleBack={handleBack}
                    isPortalPrefill={isPortalPrefill}
                  />
                )}
                {activeStep === 1 && (
                  <FormConfirmTenant handleNext={handleNext} handleBack={handleBack} />
                )}
                {activeStep === 2 && <Form2 handleNext={handleNext} handleBack={handleBack} />}
                {activeStep === 3 && <Form3 handleNext={handleNext} handleBack={handleBack} />}
                {activeStep === 4 && <Form4 handleNext={handleNext} handleBack={handleBack} />}
                {activeStep === 5 && <Form6 handleNext={handleNext} handleBack={handleBack} />}
                {activeStep === 6 && <Form5 handleNext={handleNext} handleBack={handleBack} />}
                {activeStep === 7 && (
                  <FormComments handleNext={handleNext} handleBack={handleBack} />
                )}
                {activeStep === 8 && (
                  <Form7 handleNext={handleNext} handleBack={handleBack} handleStep={handleStep} />
                )}
                {activeStep === 9 && <Success handleBack={() => setActiveStep(0)} />}
              </Fragment>
            </Box>
          </Box>
        </Paper>

        <Dialog open={Boolean(formData?.error)} onClose={() => dispatch(setErrorAction(null))}>
          <DialogContent>
            <Alert severity="error">
              <AlertTitle>Error</AlertTitle>
              {String(formData?.error || '')}
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button variant="primary" onClick={() => dispatch(setErrorAction(null))}>
              Ok
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>

      <Dialog open={policyFlag}>
        <Box
          sx={{
            padding: '30px',
          }}
        >
          <Box
            sx={{
              fontSize: '20px',
              textAlign: 'center',
            }}
          >
            There is no policies in this property !
          </Box>

          <Box
            sx={{
              marginTop: '20px',
            }}
          >
            <Button variant="primary" onClick={() => setPolicyFlag(false)}>
              Ok
            </Button>
          </Box>
        </Box>
      </Dialog>

      <Dialog
        open={discardConfirmOpen}
        onClose={() => setDiscardConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        // Layerbar uses z-index 999; keep confirm above the claim panel.
        sx={{ zIndex: 1400 }}
      >
        <DialogContent sx={{ padding: '24px 20px' }}>
          <Typography
            variant="h5"
            style={{ color: colors.purple[900], fontWeight: 600, marginBottom: 8 }}
          >
            Discard this claim?
          </Typography>
          <Typography variant="body1" style={{ color: colors.purple[900] }}>
            Your progress will be lost.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '0 20px 24px', gap: '12px' }}>
          <Button size="medium" variant="secondary" onClick={() => setDiscardConfirmOpen(false)}>
            Cancel
          </Button>
          <Button size="medium" variant="primary" onClick={exitClaimForm}>
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const CloseBar = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  margin-bottom: 4px;
`;

const CloseIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 999px;
  background: rgba(112, 37, 114, 0.1);
  color: #702572;
  cursor: pointer;
  transition: background 0.2s ease;
  padding: 0;
  margin: 0;
  flex-shrink: 0;

  &:hover {
    background: rgba(112, 37, 114, 0.16);
  }

  &:focus-visible {
    outline: 2px solid rgba(112, 37, 114, 0.45);
    outline-offset: 2px;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const mapStateToProps = (state) => {
  return {
    formData: selectClaimFormState(state),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    actions: bindActionCreators(
      {
        setDataAction,
        setInitiateAction,
        getLandlordAction,
        getApartmentAction,
        getRiderAction,
        getTenantsAction,
        checkPolicyDuplicatedAction,
      },
      dispatch,
    ),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ClaimForm);
